import { CUSTOMER_IMPORT_FROM } from '@nks/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Flex,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DetailPage } from '../../components/DetailPage';
import { ApiError } from '../../lib/api-client';
import { formatDate, orDash } from '../../lib/format';
import {
  getUnit,
  listDeliveries,
  listRegistrationCases,
  openRegistrationCase,
  scheduleDelivery,
} from './api';
import { NextServiceLine } from './NextServiceLine';

const { Text } = Typography;

/**
 * The motorcycle detail page — one half of the product's spine (CLAUDE.md §10).
 *
 * Every later module contributes a tab. Phase 2 fills Registration and
 * Delivery; Workshop and Parts land in Phases 3 and 4.
 */
export function UnitPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { uid = '' } = useParams<{ uid: string }>();

  const { data, isLoading } = useQuery({ queryKey: ['unit', uid], queryFn: () => getUnit(uid) });

  const { data: cases } = useQuery({
    queryKey: ['registration-cases', 'unit', uid],
    queryFn: () => listRegistrationCases({ q: data?.chassisNo ?? '' }),
    enabled: data !== undefined,
  });
  const { data: deliveries } = useQuery({
    queryKey: ['deliveries', 'unit', uid],
    queryFn: () => listDeliveries({ unitUid: uid }),
    enabled: data !== undefined,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['unit', uid] });
    void queryClient.invalidateQueries({ queryKey: ['registration-cases'] });
    void queryClient.invalidateQueries({ queryKey: ['deliveries'] });
  };
  const fail = (e: unknown, fallback: string): void => {
    void message.error(e instanceof ApiError ? e.message : fallback);
  };

  const open = useMutation({
    mutationFn: () => openRegistrationCase({ unitUid: uid }),
    onSuccess: (created) => {
      refresh();
      void navigate(`/registration/${created.uid}`);
    },
    onError: (e: unknown) => {
      fail(e, t('registration.openFailed'));
    },
  });

  const schedule = useMutation({
    mutationFn: () => scheduleDelivery({ unitUid: uid }),
    onSuccess: (created) => {
      refresh();
      void navigate(`/deliveries/${created.uid}`);
    },
    onError: (e: unknown) => {
      fail(e, t('delivery.scheduleFailed'));
    },
  });

  if (isLoading || data === undefined) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  const unitCase = cases?.items.find((c) => c.unitUid === uid) ?? null;
  const delivery = deliveries?.items[0] ?? null;

  return (
    <DetailPage
      title={data.chassisNo}
      subtitle={orDash(data.model)}
      tags={[
        { label: t(`vehicles.status.${data.stockStatus}`) },
        ...(data.isTestUnit ? [{ label: t('vehicles.testUnit'), colour: 'warning' }] : []),
      ]}
      onBack={() => {
        void navigate('/units');
      }}
      actions={
        <Space wrap>
          {unitCase === null && data.allocatedCustomerUid !== null && (
            <Button
              type="primary"
              loading={open.isPending}
              onClick={() => {
                open.mutate();
              }}
            >
              {t('registration.openCase')}
            </Button>
          )}
          {unitCase?.status === 'COMPLETE' && delivery === null && (
            <Button
              type="primary"
              loading={schedule.isPending}
              onClick={() => {
                schedule.mutate();
              }}
            >
              {t('delivery.schedule')}
            </Button>
          )}
        </Space>
      }
      tabs={[
        {
          key: 'overview',
          label: t('sales.tabs.overview'),
          content: (
            <Flex vertical gap={16}>
              {data.allocatedCustomerName !== null && (
                <Alert
                  type="info"
                  showIcon
                  message={t('vehicles.allocatedTitle', { name: data.allocatedCustomerName })}
                />
              )}
              {data.allocatedCustomerName === null && data.autocountSale !== null && (
                <Alert
                  type="info"
                  showIcon
                  message={
                    data.autocountSale.customer !== null ? (
                      <>
                        {t('vehicles.soldInAutocount', {
                          date: formatDate(data.autocountSale.soldOn),
                          docNo: data.autocountSale.docNo,
                        })}{' '}
                        <Link to={`/customers/${data.autocountSale.customer.uid}`}>{data.autocountSale.customer.name}</Link>
                      </>
                    ) : (
                      t(
                        data.autocountSale.soldOn < CUSTOMER_IMPORT_FROM
                          ? 'vehicles.soldInAutocountBeforeImport'
                          : 'vehicles.soldInAutocountNoBuyer',
                        {
                          date: formatDate(data.autocountSale.soldOn),
                          docNo: data.autocountSale.docNo,
                          debtorCode: data.autocountSale.debtorCode,
                          from: formatDate(CUSTOMER_IMPORT_FROM),
                        },
                      )
                    )
                  }
                />
              )}
              <Card>
                <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
                  <Descriptions.Item label={t('vehicles.fields.chassisNo')}>
                    <Text strong className="tabular">
                      {data.chassisNo}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('vehicles.fields.engineNo')}>
                    {orDash(data.engineNo)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('sales.fields.model')}>{orDash(data.model)}</Descriptions.Item>
                  <Descriptions.Item label={t('vehicles.fields.colour')}>
                    {orDash(data.colour)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('vehicles.fields.plateNo')}>
                    {orDash(data.plateNo)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('vehicles.fields.location')}>
                    {orDash(data.location)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('vehicles.fields.received')}>
                    {formatDate(data.receivedAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('vehicles.fields.age')}>
                    {data.ageDays === null ? '—' : t('vehicles.daysOnFloor', { count: data.ageDays })}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
              <NextServiceLine unitUid={uid} />
            </Flex>
          ),
        },
        {
          key: 'registration',
          label: t('customers.tabs.registration'),
          content:
            unitCase === null ? (
              <Card>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('registration.noneForUnit')} />
              </Card>
            ) : (
              <Card
                title={unitCase.caseNo}
                extra={
                  <Button
                    onClick={() => {
                      void navigate(`/registration/${unitCase.uid}`);
                    }}
                  >
                    {t('registration.openCaseLink')}
                  </Button>
                }
              >
                <Flex gap={8} wrap>
                  {unitCase.stages.map((stage) => (
                    <Tag key={stage.uid} color={stage.status === 'DONE' ? 'success' : undefined}>
                      {stage.label}: {t(`registration.stageStatus.${stage.status}`)}
                    </Tag>
                  ))}
                </Flex>
              </Card>
            ),
        },
        {
          key: 'delivery',
          label: t('delivery.title'),
          content:
            delivery === null ? (
              <Card>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('delivery.noneForUnit')} />
              </Card>
            ) : (
              <Card
                title={delivery.doNo}
                extra={
                  <Button
                    onClick={() => {
                      void navigate(`/deliveries/${delivery.uid}`);
                    }}
                  >
                    {t('delivery.openLink')}
                  </Button>
                }
              >
                <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
                  <Descriptions.Item label={t('sales.fields.status')}>
                    <Tag>{t(`delivery.status.${delivery.status}`)}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('delivery.fields.delivered')}>
                    {formatDate(delivery.deliveredAt)}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            ),
        },
        { key: 'workshop', label: t('customers.tabs.workshop'), disabled: true, content: null },
        { key: 'parts', label: t('customers.tabs.invoices'), disabled: true, content: null },
      ]}
    />
  );
}
